/**
 * AB Test: Cart Progress Bar
 *
 * Injects a free-shipping progress bar into the cart drawer, right after
 * .cart-drawer__header (i.e. immediately before .cart-drawer__body), matching
 * BBV Variation 1 — Figma desktop 4771:355, mobile 4771:494. Pure JS/CSS injection — no theme/Liquid changes,
 * following the same pattern as Complete the Look / Homepage Redesign V1.
 *
 * VARIANT ONLY: paste this file as-is into the platform's Variant code.
 * Control gets no JS at all, so the bar is simply never built — leave the
 * Control variation with no extra JS (same install pattern as the other
 * A/B tests in this project, e.g. Homepage Redesign V1).
 *
 * Local preview (no platform needed): append ?ab=control to the URL to
 * force-skip injection; omit it (or use ?ab=variant) to see the bar.
 */
(function () {
  'use strict';

  var abParam = new URLSearchParams(window.location.search).get('ab');
  if (abParam === 'control') return; // local preview escape hatch

  // VWO may evaluate variation code more than once on the same page.
  if (window.__vwoCartProgressActive) return;
  window.__vwoCartProgressActive = true;

  document.documentElement.classList.add('ab-variant');

  // ---------------------------------------------------------------------
  // Config — store-configurable values live here (no settings_schema.json
  // in this pattern; edit directly and re-paste into the platform).
  // ---------------------------------------------------------------------
  var CONFIG = {
    thresholdCents: 20000, // free shipping threshold ($200), in the shop's smallest currency unit (cents)
    color: '#C37798',
    trackColor: '#F7F5F3',
    textIncomplete: "You're {amount} away from free shipping",
    textComplete: "You've unlocked free shipping!"
  };

  // Icons exported from Figma (cart: 42x42 with its own circle background; truck: 4771:369)
  var CART_ICON_SVG =
    '<svg xmlns="http://www.w3.org/2000/svg" width="42" height="42" viewBox="0 0 42 42" fill="none">' +
    '<rect class="cart-progress__icon-background" width="42" height="42" rx="21"/>' +
    '<path d="M14.5926 15.0926H30.1481L28.2037 23.5185H16.537M28.2037 26.1111H17.1852L13.9444 12.5H12" stroke="white" stroke-width="0.8" stroke-linecap="round" stroke-linejoin="round"/>' +
    '<path d="M26.9076 30C27.6235 30 28.2039 29.4196 28.2039 28.7037C28.2039 27.9878 27.6235 27.4074 26.9076 27.4074C26.1917 27.4074 25.6113 27.9878 25.6113 28.7037C25.6113 29.4196 26.1917 30 26.9076 30Z" stroke="white" stroke-width="0.8" stroke-linecap="round" stroke-linejoin="round"/>' +
    '<path d="M18.4814 30C19.1973 30 19.7777 29.4196 19.7777 28.7037C19.7777 27.9878 19.1973 27.4074 18.4814 27.4074C17.7654 27.4074 17.1851 27.9878 17.1851 28.7037C17.1851 29.4196 17.7654 30 18.4814 30Z" stroke="white" stroke-width="0.8" stroke-linecap="round" stroke-linejoin="round"/>' +
    '</svg>';

  var TRUCK_ICON_SVG =
    '<svg width="23.65" height="18" viewBox="0 0 24.0502 18.3" fill="none" xmlns="http://www.w3.org/2000/svg">' +
    '<path d="M4.78802 17.1678C4.32905 16.699 4.0712 16.0631 4.0712 15.4001C4.0712 14.737 4.32905 14.1011 4.78802 13.6323C5.247 13.1635 5.8695 12.9001 6.51859 12.9001C7.16767 12.9001 7.79018 13.1635 8.24915 13.6323C8.70813 14.1011 8.96598 14.737 8.96598 15.4001C8.96598 16.0631 8.70813 16.699 8.24915 17.1678C7.79018 17.6367 7.16767 17.9 6.51859 17.9C5.8695 17.9 5.247 17.6367 4.78802 17.1678Z" stroke="white" stroke-width="0.8" stroke-linecap="round" stroke-linejoin="round"/>' +
    '<path d="M17.025 17.1678C16.566 16.699 16.3081 16.0631 16.3081 15.4001C16.3081 14.737 16.566 14.1011 17.025 13.6323C17.4839 13.1635 18.1064 12.9001 18.7555 12.9001C19.4046 12.9001 20.0271 13.1635 20.4861 13.6323C20.9451 14.1011 21.2029 14.737 21.2029 15.4001C21.2029 16.0631 20.9451 16.699 20.4861 17.1678C20.0271 17.6367 19.4046 17.9 18.7555 17.9C18.1064 17.9 17.4839 17.6367 17.025 17.1678Z" stroke="white" stroke-width="0.8" stroke-linecap="round" stroke-linejoin="round"/>' +
    '<path d="M4.07108 15.3999H1.62369V10.3999M0.4 0.4H13.8606V15.3999M8.96586 15.3999H16.308M13.8606 1.64999H19.9791L23.6502 7.89996V15.3999H21.2028M23.6502 7.89996H13.8606M1.62369 5.39997H6.51847" stroke="white" stroke-width="0.8" stroke-linecap="round" stroke-linejoin="round"/>' +
    '</svg>';

  // Last rendered state, so a bar rebuilt by the theme's drawer re-render is
  // painted with these values immediately instead of flashing empty (0%).
  var lastRenderedPercent = null;
  var lastMessage = '';
  var lastAmounts = '';
  var lastIsUnlocked = false;
  var lastChangeTime = 0;
  var PULSE_DURATION_MS = 400; // must match the pulse animation duration in bar.css

  // Multi-currency support: reuse the shop's own money_format (e.g. "₹{{amount}}",
  // "${{amount_with_comma_separator}}"), already exposed by the theme at
  // window.theme.settings.product.moneyFormat (see snippets/theme-settings.liquid).
  // Falls back to a plain "$" format if that global isn't present for any reason.
  function formatMoney(cents) {
    var format =
      (window.theme && window.theme.settings && window.theme.settings.product &&
        window.theme.settings.product.moneyFormat) || '${{amount}}';

    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    var match = format.match(placeholderRegex);
    if (!match) return '$' + ((cents || 0) / 100).toFixed(2);

    function withDelimiters(number, precision, thousands, decimal) {
      thousands = thousands || ',';
      decimal = decimal || '.';
      if (isNaN(number) || number === null) return '0';
      number = (number / 100).toFixed(precision);
      var parts = number.split('.');
      var whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      var frac = parts[1] ? decimal + parts[1] : '';
      return whole + frac;
    }

    var value;
    switch (match[1]) {
      case 'amount_no_decimals':
        value = withDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = withDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = withDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = withDelimiters(cents, 0, ' ');
        break;
      case 'amount_with_apostrophe_separator':
        value = withDelimiters(cents, 2, "'");
        break;
      default:
        value = withDelimiters(cents, 2);
    }

    // Figma shows whole amounts as "$65 / $200" — drop a trailing ".00"/",00"
    value = value.replace(/[.,]00$/, '');

    return format.replace(placeholderRegex, value);
  }

  // The bar is only for US shoppers ($200 threshold). Read the region from the
  // currency switcher's selected value first (what the shopper actually sees),
  // then fall back to Shopify's own currency / country globals.
  function isUsRegion() {
    try {
      var selectedEls = document.querySelectorAll('.bucks-selected');
      for (var i = 0; i < selectedEls.length; i++) {
        var text = (selectedEls[i].textContent || '').trim().toUpperCase();
        // Widgets may finish loading with a label such as "USD $".
        if (/\bUSD\b/.test(text)) return true;
        if (/\b[A-Z]{3}\b/.test(text)) return false;
      }
      if (window.Shopify) {
        if (window.Shopify.currency && window.Shopify.currency.active) {
          return String(window.Shopify.currency.active).toUpperCase() === 'USD';
        }
        if (window.Shopify.country) {
          return String(window.Shopify.country).toUpperCase() === 'US';
        }
      }
    } catch (err) { }
    return false;
  }

  function buildBar() {
    var el = document.createElement('div');
    el.className = 'cart-progress';
    el.id = 'CartProgress';
    el.style.setProperty('--cp-color', CONFIG.color);
    el.style.setProperty('--cp-track', CONFIG.trackColor);
    if (lastIsUnlocked) el.classList.add('cart-progress--unlocked');
    // A bar rebuilt mid-pulse continues the same pulse (negative delay = time
    // already elapsed) instead of restarting it, so the dot pulses only once.
    var elapsedMs = Date.now() - lastChangeTime;
    if (elapsedMs < PULSE_DURATION_MS) {
      el.style.setProperty('--cp-pulse-offset', '-' + elapsedMs + 'ms');
      el.classList.add('is-updated');
    }
    var initialWidth = lastRenderedPercent === null ? 0 : lastRenderedPercent;
    el.innerHTML =
      '<p class="cart-progress__message" id="CartProgressMessage" role="status" aria-live="polite">' + lastMessage + '</p>' +
      '<div class="cart-progress__track-row">' +
      '<span class="cart-progress__icon cart-progress__icon--start" aria-hidden="true">' + CART_ICON_SVG + '</span>' +
      '<div class="cart-progress__track">' +
      '<div class="cart-progress__fill" id="CartProgressFill" style="width:' + initialWidth + '%">' +
      '<span class="cart-progress__dot"></span>' +
      '</div>' +
      '</div>' +
      '<span class="cart-progress__icon cart-progress__icon--end" aria-hidden="true">' + TRUCK_ICON_SVG + '</span>' +
      '</div>' +
      '<div class="cart-progress__amounts">' +
      '<span class="cart-progress__amount-text" id="CartProgressAmounts">' + lastAmounts + '</span>' +
      '</div>';

    return el;
  }

  function ensureInserted() {
    var drawer = document.getElementById('Cart-Drawer');
    if (!drawer) return null;

    var existing = drawer.querySelector('#CartProgress');

    // Non-US region: make sure no bar is shown.
    if (!isUsRegion()) {
      if (existing) existing.remove();
      return null;
    }

    // The theme replaces .cart-drawer__body's ancestor markup wholesale on
    // every quantity/add-to-cart change (cart-drawer.js _applySectionUpdates
    // re-renders the whole `.cart-drawer` section), which wipes out anything
    // we injected. Detect that by checking the node is still attached to
    // THIS drawer element, not just present anywhere.
    if (existing && drawer.contains(existing)) return existing;

    var body = drawer.querySelector('.cart-drawer__body');
    var header = drawer.querySelector('.cart-drawer__header');
    if (!body && !header) return null;

    var bar = buildBar();
    if (body) {
      body.parentNode.insertBefore(bar, body);
    } else {
      header.insertAdjacentElement('afterend', bar);
    }
    return bar;
  }

  function render(currentCents) {
    var bar = ensureInserted();
    if (!bar) return;

    var threshold = CONFIG.thresholdCents;
    var remaining = threshold - currentCents;
    var unlocked = remaining <= 0;
    if (unlocked) remaining = 0;

    var percent = threshold > 0 ? (currentCents / threshold) * 100 : 0;
    if (percent > 100) percent = 100;
    if (percent < 0) percent = 0;

    bar.classList.toggle('cart-progress--unlocked', unlocked);

    var message = unlocked
      ? CONFIG.textComplete
      : CONFIG.textIncomplete.replace('{amount}', formatMoney(remaining));
    var amounts = formatMoney(currentCents) + ' / ' + formatMoney(threshold);

    var msgEl = bar.querySelector('#CartProgressMessage');
    if (msgEl && msgEl.textContent !== message) msgEl.textContent = message;

    var amountsEl = bar.querySelector('#CartProgressAmounts');
    if (amountsEl && amountsEl.textContent !== amounts) amountsEl.textContent = amounts;

    // Simple CSS transition: the fill already holds the previous percent.
    var fillEl = bar.querySelector('#CartProgressFill');
    if (fillEl) fillEl.style.width = percent + '%';

    if (percent !== lastRenderedPercent) {
      lastChangeTime = Date.now();
      // Restart the pulse: remove, force reflow, re-add
      bar.style.setProperty('--cp-pulse-offset', '0ms');
      bar.classList.remove('is-updated');
      bar.getBoundingClientRect();
      bar.classList.add('is-updated');
    }

    lastRenderedPercent = percent;
    lastMessage = message;
    lastAmounts = amounts;
    lastIsUnlocked = unlocked;
  }

  // -----------------------------------------------------------------------
  // Cart total source: try the theme's own event payload first (cheap, no
  // network call), fall back to an independent /cart.js fetch when that
  // isn't available yet (e.g. first render, before any change has happened).
  // -----------------------------------------------------------------------
  function renderFromCartObject(cart) {
    if (!cart) return;
    var cents = typeof cart.total_price === 'number'
      ? cart.total_price
      : (typeof cart.items_subtotal_price === 'number' ? cart.items_subtotal_price : null);
    if (cents === null) return;
    render(cents);
  }

  var fetchPending = false;
  var fetchAgain = false;
  function fetchAndRender() {
    if (!isUsRegion()) {
      ensureInserted(); // removes a leftover bar if the region changed
      return;
    }
    if (fetchPending) {
      fetchAgain = true;
      return;
    }
    fetchPending = true;
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    fetch(root.replace(/\/?$/, '/') + 'cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then(function (r) {
        if (!r.ok) throw new Error('Cart request failed');
        return r.json();
      })
      .then(renderFromCartObject)
      .catch(function () { })
      .then(function () {
        fetchPending = false;
        if (fetchAgain) {
          fetchAgain = false;
          fetchAndRender();
        }
      });
  }

  document.addEventListener('line-item:change:end', function (e) {
    try {
      var cart = e && e.detail && e.detail.cart;
      if (cart) {
        renderFromCartObject(cart);
      } else {
        fetchAndRender();
      }
    } catch (err) {
      fetchAndRender();
    }
  });

  document.addEventListener('cart-drawer:open', function () {
    try {
      // Re-insert immediately (in case markup was wiped since last render)
      // and refresh from the network in case the drawer was opened without
      // any 'line-item:change:end' having fired yet (e.g. very first load).
      ensureInserted();
    } catch (err) { }
    fetchAndRender();
  });

  // -----------------------------------------------------------------------
  // Watchdog: the theme wipes/rebuilds #Cart-Drawer's children wholesale on
  // every cart change (innerHTML replace, not a targeted patch), which can
  // silently drop our injected bar if the above event listeners ever miss a
  // beat (timing races, a platform sandboxing custom events, etc). Rather
  // than rely solely on catching every code path that causes a wipe, watch
  // the drawer directly and self-heal whenever our node goes missing.
  // -----------------------------------------------------------------------
  function startWatchdog() {
    // Observe a stable ancestor: the theme can replace the drawer itself,
    // or insert it after this variation has already initialized.
    var pending = false;
    var observer = new MutationObserver(function () {
      if (pending) return;
      pending = true;
      requestAnimationFrame(function () {
        pending = false;
        try {
          var drawer = document.getElementById('Cart-Drawer');
          var existing = drawer && drawer.querySelector('#CartProgress');
          var bar = ensureInserted();
          // Our own text/markup updates also trigger this observer.
          // Only a newly restored bar needs a cart request.
          if (bar && bar !== existing) fetchAndRender();
        } catch (err) { }
      });
    });
    observer.observe(document.documentElement, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['class']
    });
  }

  function init() {
    fetchAndRender();
    startWatchdog();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
